import * as React from 'react'
import { SettingsSectionWithTabs } from '../../components/SettingsSectionWithTabs'

import { useLocalize } from '@/modules/shared/context/localize/LocalizeContext'
import axios from 'axios'
import { toast } from '@/lib/toasts/api'
import { SettingsPageWrapper } from '../../components/SettingsPageWrapper'
import { usePersistentTab } from '@/modules/shared/stores/tabsStore'
import { useQueryEmailConfigurationSettings } from '@/lib/api/company/configuration/emailConfiguration/useQueryEmailConfigurationSettings'
import { useMutationEmailConfigurationSettings } from '@/lib/api/company/configuration/emailConfiguration/useMutationEmailConfigurationSettings'
import {
  EmailConfigurationModel,
  EmailConfigurationRecipientSettingsKeys,
  EmailRecipientSettings,
} from '@/lib/api/company/configuration/emailConfiguration/types'
import { EmailRecipientSection } from './EmailRecipientSection'
import { RouteComponentProps } from '@/app/navigation/withRouter'

export const EmailSettingsPageContent = ({
  match,
}: RouteComponentProps<{ companyId: string }>) => {
  const readOnly = false
  const companyId = match.params.companyId
  const [persistedTab, setSelectedTab] = usePersistentTab(
    'email-settings',
    'r1',
  )
  // Zakładka serwera SMTP jest wyłączona; stary wybór "basic" nie ma już czego pokazać.
  const selectedTab = persistedTab === 'basic' ? 'r1' : persistedTab

  const {
    data,
    isFetching,
    error: queryError,
  } = useQueryEmailConfigurationSettings({ companyId })

  const {
    mutateAsync,
    isPending,
    error: mutationError,
  } = useMutationEmailConfigurationSettings(companyId)

  const handleSave = async () => {
    if (state) {
      try {
        const resp = await mutateAsync(state)
        toast.success({ text: t('configuration.data-updated') })
      } catch (e) {
        if (axios.isAxiosError(e)) {
          const data = e.response?.data
          if (Array.isArray(data) && data.length > 0) {
            const code = data[0]?.code
            let errorMsg = code
            try {
              errorMsg = t(`errorCode.${errorMsg}`)
            } catch (e) {}
            console.log('API error code:', code, errorMsg)
            toast.error({ text: errorMsg, autoCloseMs: 5000 })
          } else {
            console.log('Axios error, but response data is not an array:', data)
            toast.error({ text: 'Unhandled Error' })
          }
        } else {
          // Non-Axios error
          console.error('Unknown error:', e)
          toast.error({ text: 'Unknown Error' })
        }
      }
    }
  }

  const handleReset = React.useCallback(() => {
    // setState(data)
  }, [data])

  const [state, setState] = React.useState<EmailConfigurationModel | null>(null)

  React.useEffect(() => {
    if (data) {
      setState(data)
    }
  }, [data])

  const { t } = useLocalize()

  const diffLabels = React.useMemo<Record<string, string>>(() => ({
    mailClient: t('configuration-email.tab-mail-client'),
    recipient1: `${t('configuration-email.tab-recipient')} 1`,
    recipient2: `${t('configuration-email.tab-recipient')} 2`,
    recipient3: `${t('configuration-email.tab-recipient')} 3`,
    imiSubmission: t('configuration-email.tab-imi-submission'),
    smtpServer: t('configuration-email.smtp-server'),
    smtpPort: t('configuration-email.smtp-port'),
    username: t('configuration-email.username'),
    password: t('configuration-email.password'),
    senderEmail: t('configuration-email.sender-email'),
    imapServer: t('configuration-email.imap-server'),
    recipientEmail: t('configuration-email.recipient-email'),
    emailSubject: t('configuration-email.email-subject'),
    ccEmail: t('configuration-email.cc-email'),
    bccEmail: t('configuration-email.bcc-email'),
    attachmentName: t('configuration-email.attachment-name'),
    defaultPassword: t('configuration-email.default-password'),
    emailBody: t('configuration-email.email-body'),
    footer: t('configuration-email.footer'),
  }), [t])

  const handleSectionChange = React.useCallback(
    (
      name: EmailConfigurationRecipientSettingsKeys,
      sectionState: EmailRecipientSettings,
    ) => {
      setState((state) => ({
        ...state,
        [name]: sectionState,
      }))
    },
    [],
  )

  const isDirty = React.useMemo(() => {
    return JSON.stringify(state) !== JSON.stringify(data)
  }, [state, data])

  return (
    <SettingsPageWrapper
      isReady={!!state}
      isPending={isPending || isFetching}
      isError={!!queryError || !!mutationError}
      errorMsg={
        queryError ? t('configuration-email.load-error') : ''
      }
      isDirty={isDirty}
      handleSave={handleSave}
      handleReset={handleReset}
      globalErrorMsg={t('configuration-email.load-error')}
      diffBefore={data as Record<string, unknown>}
      diffAfter={state as Record<string, unknown>}
      diffLabels={diffLabels}
    >
      {state && (
        <SettingsSectionWithTabs
          title=""
          selected={selectedTab}
          setSelected={setSelectedTab}
        >
          {/* Serwer pocztowy jest nasz (Postmark, noreply@easytacho.pl).
              Zakładka dodawania SMTP klienta zostaje wyłączona.
          <div
            data-tab-name="basic"
            title={t('configuration-email.tab-mail-client')}
            className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
          >
            <EmailClientSection
              name="mailClient"
              sectionState={state.mailClient}
              handleSectionStateChange={handleClientSectionChange}
            />
          </div>
          */}
          <div
            data-tab-name="r1"
            title={`${t('configuration-email.tab-recipient')} 1`}
            className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
          >
            <EmailRecipientSection
              name="recipient1"
              sectionState={state.recipient1}
              handleSectionStateChange={handleSectionChange}
            />
          </div>
          <div
            data-tab-name="r2"
            title={`${t('configuration-email.tab-recipient')} 2`}
            className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
          >
            <EmailRecipientSection
              name="recipient2"
              sectionState={state.recipient2}
              handleSectionStateChange={handleSectionChange}
            />
          </div>
          <div
            data-tab-name="r3"
            title={`${t('configuration-email.tab-recipient')} 3`}
            className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
          >
            <EmailRecipientSection
              name="recipient3"
              sectionState={state.recipient3}
              handleSectionStateChange={handleSectionChange}
            />
          </div>
          <div
            data-tab-name="imi"
            title={t('configuration-email.tab-imi-submission')}
            className="tw-flex tw-flex-col tw-gap-6 tw-p-2 tw-pb-8"
          >
            <EmailRecipientSection
              name="imiSubmission"
              sectionState={state.imiSubmission}
              handleSectionStateChange={handleSectionChange}
            />
          </div>
        </SettingsSectionWithTabs>
      )}
    </SettingsPageWrapper>
  )
}
